import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useTheme,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  KeyboardArrowRight,
  Lock,
  Dashboard,
  Home, 
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import {
  MENU_SECTIONS,
  type MenuSection,
  type MenuGroupItem,
  type MenuLeafItem,
  type MenuEntry,
} from './menuConfig';
import { useWorkflows } from '../../hooks/useTasksApi';
import { useThemeMode } from '../../contexts/ThemeContext';
import { usePermission } from '../../hooks/usePermissions';
import { usePendingLeavesCount } from '../../hooks/usePendingLeavesCount';
import { timesheetService } from '../../services/timesheetService';
import { useEmployeeNotifications } from '../../hooks/useEmployeeNotifications';

interface SidebarNavigationProps {
  onNavigate: (path: string) => void;
}

interface LeafItemProps {
  item: MenuLeafItem;
  level: number;
  active: boolean;
  onNavigate: (path: string) => void;
  pendingLeavesCount: number;
  timesheetsCount: number;
  canManageTimesheets: boolean;
  rejectedLeavesCount: number;
  rejectedTimesheetsCount: number;
}

const hasActivePath = (entries: MenuEntry[], currentPath: string): boolean => {
  for (const entry of entries) {
    if (entry.kind === 'leaf' && entry.path === currentPath) {
      return true;
    }
    if (entry.kind === 'group' && hasActivePath(entry.children, currentPath)) {
      return true;
    }
  }
  return false;
};

const areAllEntriesDisabled = (entries: MenuEntry[]): boolean => {
  return entries.every((entry) => {
    if (entry.kind === 'leaf') return entry.disabled;
    return areAllEntriesDisabled(entry.children);
  });
};

const LeafItem = ({ 
  item, 
  level, 
  active, 
  onNavigate, 
  pendingLeavesCount, 
  timesheetsCount, 
  canManageTimesheets,
  rejectedLeavesCount,
  rejectedTimesheetsCount 
}: LeafItemProps) => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  
  const Icon = item.icon;
  const isLevel3 = item.id.startsWith('kanban-') || level >= 2;

  const calculatedPaddingLeft = 3 + level * 2.5;

  const activeBgColor = mode === 'dark' ? 'rgba(56, 189, 248, 0.12)' : 'rgba(2, 132, 199, 0.08)';
  const activeTextColor = mode === 'dark' ? '#38bdf8' : '#0284c7';
  const activeGradient = mode === 'dark' 
    ? 'linear-gradient(90deg, rgba(56, 189, 248, 0.15) 0%, rgba(56, 189, 248, 0.02) 100%)' 
    : 'linear-gradient(90deg, rgba(2, 132, 199, 0.1) 0%, rgba(2, 132, 199, 0.01) 100%)';
  
  const isLeaveRequest = ['leave-requests', 'demandes-conges', 'conges', 'employee-leaves'].includes(item.id);
  const isTimesheet = ['timesheets', 'feuilles-de-temps', 'employees-timesheets'].includes(item.id);

  const badgeCount = useMemo(() => {
    if (isLeaveRequest) {
      return canManageTimesheets ? pendingLeavesCount : rejectedLeavesCount;
    }
    if (isTimesheet) {
      return canManageTimesheets ? timesheetsCount : rejectedTimesheetsCount;
    }
    return 0;
  }, [isLeaveRequest, isTimesheet, canManageTimesheets, pendingLeavesCount, rejectedLeavesCount, timesheetsCount, rejectedTimesheetsCount]);

  return (
    <ListItem disablePadding>
      <ListItemButton
        disabled={item.disabled}
        onClick={() => {
          if (item.disabled) return;
          if (item.id === 'employee-leaves' && !canManageTimesheets) {
            localStorage.setItem('last_viewed_rejected_leaves', new Date().toISOString());
          }
          onNavigate(item.path);
        }}
        sx={{
          minHeight: isLevel3 ? 30 : level > 0 ? 38 : 48,
          pl: calculatedPaddingLeft,
          pr: 2,
          py: isLevel3 ? 0.1 : level > 0 ? 0.25 : 1,
          borderLeft: '3px solid transparent',
          transition: 'all 0.15s ease',
          ...(active && {
            bgcolor: activeBgColor,
            color: activeTextColor,
            borderLeftColor: activeTextColor,
            backgroundImage: activeGradient,
          }),
          ...(!active && !item.disabled && {
            color: isLevel3
              ? (mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(15, 23, 42, 0.7)')
              : theme.palette.text.secondary,
            '&:hover': {
              bgcolor: theme.palette.action.hover,
              color: theme.palette.text.primary,
              borderLeftColor: theme.palette.divider,
            },
          }),
          ...(item.disabled && {
            opacity: 0.45,
            cursor: 'default',
          }),
        }}
      >
        <ListItemIcon
          sx={{
            color: 'inherit',
            minWidth: isLevel3 ? 28 : level > 0 ? 32 : 38,
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: isLevel3 ? '0.75rem' : level > 0 ? '0.95rem' : '1.3rem' }} />
        </ListItemIcon>

        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{item.label}</span>
              {badgeCount > 0 && (
                <Chip
                  label={badgeCount > 99 ? '99+' : badgeCount}
                  size="small"
                  color="error"
                  variant="filled"
                  sx={{
                    height: 18,
                    minWidth: 18,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    borderRadius: '9px', 
                    backgroundColor: '#e11d48', 
                    '& .MuiChip-label': {
                      px: 0.5,
                      py: 0,
                    },
                  }}
                />
              )}
            </Box>
          }
          primaryTypographyProps={{
            component: 'div',
            fontWeight: active ? 600 : 500,
            fontSize: isLevel3 ? '0.75rem' : level > 0 ? '0.82rem' : '0.95rem',
            color: 'inherit',
          }}
        />

        {active && level === 0 && (
          <KeyboardArrowRight sx={{ fontSize: '1.1rem', opacity: 0.5, ml: 1 }} />
        )}

        {item.disabled && (
          <Lock sx={{ fontSize: '0.85rem', opacity: 0.35, ml: 0.5 }} />
        )}
      </ListItemButton>
    </ListItem>
  );
};

interface GroupItemProps {
  item: MenuGroupItem;
  onNavigate: (path: string) => void;
  currentPath: string;
  level: number;
  pendingLeavesCount: number;
  timesheetsCount: number;
  canManageTimesheets: boolean;
  rejectedLeavesCount: number;
  rejectedTimesheetsCount: number;
}

const GroupItem = ({ 
  item, 
  onNavigate, 
  currentPath, 
  level, 
  pendingLeavesCount, 
  timesheetsCount, 
  canManageTimesheets,
  rejectedLeavesCount,
  rejectedTimesheetsCount 
}: GroupItemProps) => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const Icon = item.icon;

  const hasActiveChild = hasActivePath(item.children, currentPath);
  const [open, setOpen] = useState(hasActiveChild);
  const handleToggle = useCallback(() => setOpen((o) => !o), []);
  const allDisabled = areAllEntriesDisabled(item.children);

  const calculatedPaddingLeft = 3 + level * 2.5;
  const primaryColor = mode === 'dark' ? '#38bdf8' : '#0284c7';

  const groupBadgeCount = useMemo(() => {
    let count = 0;
    const checkChildren = (children: MenuEntry[]) => {
      for (const child of children) {
        if (child.kind === 'leaf') {
          if (['leave-requests', 'demandes-conges', 'conges', 'employee-leaves'].includes(child.id)) {
            count += canManageTimesheets ? pendingLeavesCount : rejectedLeavesCount;
          }
          if (['timesheets', 'feuilles-de-temps', 'employees-timesheets'].includes(child.id)) {
            count += canManageTimesheets ? timesheetsCount : rejectedTimesheetsCount;
          }
        } else if (child.kind === 'group') {
          checkChildren(child.children);
        }
      }
    };
    checkChildren(item.children);
    return count;
  }, [item.children, canManageTimesheets, pendingLeavesCount, rejectedLeavesCount, timesheetsCount, rejectedTimesheetsCount]);

  const showBadgeOnParent = useMemo(() => {
    return groupBadgeCount > 0 && !open;
  }, [groupBadgeCount, open]);

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={handleToggle}
          sx={{
            minHeight: level > 0 ? 36 : 48,
            pl: calculatedPaddingLeft,
            pr: 2,
            py: level > 0 ? 0.25 : 1,
            borderLeft: '3px solid transparent',
            transition: 'all 0.15s ease',
            ...(hasActiveChild && {
              borderLeftColor: primaryColor,
            }),
            color: allDisabled
              ? theme.palette.text.disabled
              : theme.palette.text.primary,
            '&:hover': {
              bgcolor: theme.palette.action.hover,
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: hasActiveChild ? primaryColor : 'inherit',
              minWidth: level > 0 ? 32 : 38,
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: level > 0 ? '0.95rem' : '1.3rem' }} />
          </ListItemIcon>

          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <span>{item.label}</span>
                {showBadgeOnParent && (
                  <Chip
                    label={groupBadgeCount > 99 ? '99+' : groupBadgeCount}
                    size="small"
                    color="error"
                    variant="filled"
                    sx={{
                      height: 18,
                      minWidth: 18,
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      borderRadius: '9px',
                      backgroundColor: '#e11d48',
                      '& .MuiChip-label': {
                        px: 0.5,
                        py: 0,
                      },
                    }}
                  />
                )}
              </Box>
            }
            primaryTypographyProps={{
              component: 'div',
              fontWeight: hasActiveChild ? 700 : 600,
              fontSize: level > 0 ? '0.78rem' : '0.9rem',
              color: 'inherit',
              letterSpacing: '-0.01em',
            }}
          />

          {open ? (
            <ExpandLess sx={{ fontSize: level > 0 ? '1rem' : '1.2rem', opacity: 0.5 }} />
          ) : (
            <ExpandMore sx={{ fontSize: level > 0 ? '1rem' : '1.2rem', opacity: 0.5 }} />
          )}
        </ListItemButton>
      </ListItem>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.children.map((child) =>
            child.kind === 'group' ? (
              <GroupItem
                key={child.id}
                item={child}
                onNavigate={onNavigate}
                currentPath={currentPath}
                level={level + 1}
                pendingLeavesCount={pendingLeavesCount}
                timesheetsCount={timesheetsCount}
                canManageTimesheets={canManageTimesheets}
                rejectedLeavesCount={rejectedLeavesCount}
                rejectedTimesheetsCount={rejectedTimesheetsCount}
              />
            ) : (
              <LeafItem
                key={child.id}
                item={child}
                level={level + 1}
                active={currentPath === child.path}
                onNavigate={onNavigate}
                pendingLeavesCount={pendingLeavesCount}
                timesheetsCount={timesheetsCount}
                canManageTimesheets={canManageTimesheets}
                rejectedLeavesCount={rejectedLeavesCount}
                rejectedTimesheetsCount={rejectedTimesheetsCount}
              />
            ),
          )}
        </List>
      </Collapse>
    </>
  );
};

interface SectionProps {
  section: MenuSection;
  onNavigate: (path: string) => void;
  currentPath: string;
  pendingLeavesCount: number;
  timesheetsCount: number;
  canManageTimesheets: boolean;
  rejectedLeavesCount: number;
  rejectedTimesheetsCount: number;
}

const Section = ({ 
  section, 
  onNavigate, 
  currentPath, 
  pendingLeavesCount, 
  timesheetsCount, 
  canManageTimesheets,
  rejectedLeavesCount,
  rejectedTimesheetsCount 
}: SectionProps) => {
  const theme = useTheme();

  return (
    <Box>
      {section.sectionLabel && (
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            px: 3,
            pt: 2,
            pb: 0.5,
            fontSize: '0.65rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: theme.palette.text.disabled,
            textTransform: 'uppercase',
            userSelect: 'none',
          }}
        >
          {section.sectionLabel}
        </Typography>
      )}

      <List disablePadding>
        {section.entries.map((entry) =>
          entry.kind === 'group' ? (
            <GroupItem
              key={entry.id}
              item={entry}
              onNavigate={onNavigate}
              currentPath={currentPath}
              level={0}
              pendingLeavesCount={pendingLeavesCount}
              timesheetsCount={timesheetsCount}
              canManageTimesheets={canManageTimesheets}
              rejectedLeavesCount={rejectedLeavesCount}
              rejectedTimesheetsCount={rejectedTimesheetsCount}
            />
          ) : (
            <LeafItem
              key={entry.id}
              item={entry}
              level={0}
              active={currentPath === entry.path}
              onNavigate={onNavigate}
              pendingLeavesCount={pendingLeavesCount}
              timesheetsCount={timesheetsCount}
              canManageTimesheets={canManageTimesheets}
              rejectedLeavesCount={rejectedLeavesCount}
              rejectedTimesheetsCount={rejectedTimesheetsCount}
            />
          ),
        )}
      </List>
    </Box>
  );
};

const SidebarNavigation = ({ onNavigate }: SidebarNavigationProps) => {
  const location = useLocation();
  const { data: workflows = [] } = useWorkflows();
  const { hasPermission, hasNavAccess } = usePermission();
  const { rejectedLeavesCount, rejectedTimesheetsCount } = useEmployeeNotifications();
  const pendingLeavesCount = usePendingLeavesCount();

  const [timesheetsEnAttenteCount, setTimesheetsEnAttenteCount] = useState(0);

  const theme = useTheme();
  const { mode } = useThemeMode();

  const canViewDashboard = hasNavAccess('employees') || hasPermission('can_manage_users');
  const canManageTimesheets = hasNavAccess('timesheets') || hasNavAccess('leave_requests') || hasPermission('can_view_employee');

  const isDashboardActive = location.pathname === '/' || location.pathname === '/dashboard';
  const isAccueilActive = location.pathname === '/accueil';

  const activeBgColor = mode === 'dark' ? 'rgba(56, 189, 248, 0.12)' : 'rgba(2, 132, 199, 0.08)';
  const activeTextColor = mode === 'dark' ? '#38bdf8' : '#0284c7';
  const activeGradient = mode === 'dark' 
    ? 'linear-gradient(90deg, rgba(56, 189, 248, 0.15) 0%, rgba(56, 189, 248, 0.02) 100%)' 
    : 'linear-gradient(90deg, rgba(2, 132, 199, 0.1) 0%, rgba(2, 132, 199, 0.01) 100%)';

useEffect(() => {
    // Si l'utilisateur n'a pas les droits de gestion des feuilles de temps, on bloque l'appel
    if (!canManageTimesheets) {
      setTimesheetsEnAttenteCount(0);
      return;
    }

    const fetchTimesheetsCount = async () => {
      try {
        const timesheets = await timesheetService.getAll();
        if (Array.isArray(timesheets)) {
          const count = timesheets.reduce((acc, t: any) => {
            const status = String(t.status).toLowerCase();
            return (status === 'submitted' || status === 'soumise') ? acc + 1 : acc;
          }, 0);
          setTimesheetsEnAttenteCount(count);
        }
      } catch (err) {
        console.error("Erreur chargement timesheets Sidebar", err);
      }
    };

    fetchTimesheetsCount();
    window.addEventListener('timesheetStatusUpdated', fetchTimesheetsCount);

    return () => {
      window.removeEventListener('timesheetStatusUpdated', fetchTimesheetsCount);
    };
  }, [canManageTimesheets]);

  const kanbanWorkflows = useMemo(() => {
    return workflows.filter((w) => w.workflow_type !== 'lead');
  }, [workflows]);

 const dynamicSections = useMemo<MenuSection[]>(() => {
    const isManagerOrAdmin = hasNavAccess('employees') || hasPermission('can_manage_users') || hasPermission('can_manage_roles');
    const isManager = hasNavAccess('timesheets') || hasNavAccess('leave_requests') || hasNavAccess('managed_department_employees');
    
    // --- NOUVEAU : Récupération du drapeau HR global ---
    const hasHumanResourcesAccess = hasNavAccess('human_resources');
 
    return MENU_SECTIONS.map((section) => {
      const filteredEntries = section.entries
        .map((entry) => {
          if (entry.id === 'securite' && isManagerOrAdmin) {
            return null;
          }
          if (entry.id === 'facturation') {
            if (!hasPermission('can_view_invoice')) return null;
          }
          
          // --- GESTION DES RESSOURCES HUMAINES ---
          if (entry.id === 'ressources-humaines' && entry.kind === 'group') {
            const hasFullHrAccess = hasNavAccess('human_resources'); 
            const canViewManagedEmployees = hasNavAccess('managed_department_employees');
            const canViewManagedPlanning = hasNavAccess('managed_department_planning');
            const canViewManagedTimesheets = hasNavAccess('managed_department_timesheets');
            const canViewManagedLeaves = hasNavAccess('managed_department_leave_requests');

            // Si aucun accès ni global ni managé, on cache tout le groupe
            if (!hasFullHrAccess && !canViewManagedEmployees && !canViewManagedPlanning && !canViewManagedTimesheets && !canViewManagedLeaves) {
              return null;
            }

            const rhChildrenFiltered = entry.children
              .map((child) => {
                // Si l'utilisateur a l'accès HR global complet, il voit tout
                if (hasFullHrAccess) {
                  return child;
                }

                // Sinon, on filtre précisément chaque sous-onglet selon son objet "navigation" managé
                if (child.id === 'employees-liste' && (canViewManagedEmployees || hasNavAccess('employees'))) {
                  return child;
                }
                if (child.id === 'employees-timesheets' && (canViewManagedTimesheets || hasNavAccess('timesheets'))) {
                  return child;
                }
                if (child.id === 'leave-requests' && (canViewManagedLeaves || hasNavAccess('leave_requests'))) {
                  return child;
                }
              if (
                  child.id === 'employees-planning' && 
                  (canViewManagedPlanning || hasNavAccess('planning') || hasNavAccess('managed_department_planning'))
                ) {
                  return child;
                }

                return null;
              })
              .filter((child): child is MenuEntry => child !== null);

            if (rhChildrenFiltered.length === 0) return null;
            return { ...entry, children: rhChildrenFiltered };
          }

       if (entry.id === 'espace-temps') {
            const isGlobalAdmin = hasPermission('can_manage_users') && hasNavAccess('human_resources');
            if (isGlobalAdmin) return null; 
          }
          if (entry.id === 'configuration') {
            if (!hasPermission('can_manage_roles')) return null;
          }

          // --- SECTION ADMINISTRATION (Strictement réservée aux Admins globaux) ---
          if (entry.id === 'administration' && entry.kind === 'group') {
            const isAdminGlobal = hasPermission('can_manage_users') || hasPermission('can_manage_roles');
            if (!isAdminGlobal) return null;
            return entry;
          }
          
          if (entry.kind === 'group' && entry.id === 'production') {
            const childrenFiltered = entry.children
              .map((child) => {
                if (child.kind === 'group' && child.id === 'kanban') {
                  return {
                    ...child,
                    children: kanbanWorkflows.map((workflow) => ({
                      kind: 'leaf' as const,
                      id: `kanban-${workflow.id}`,
                      label: workflow.name,
                      path: `/kanban/${workflow.id}`,
                      icon: child.icon,
                      disabled: false,
                    })),
                  };
                }
                if (child.kind === 'leaf' && child.id === 'soumissions') {
                  return hasPermission('can_view_soumission') ? child : null;
                }
                return child;
              })
              .filter(Boolean) as MenuEntry[];

            return { ...entry, children: childrenFiltered };
          }

          return entry;
        })
        .filter((entry): entry is MenuEntry => entry !== null);

      return {
        ...section,
        entries: filteredEntries,
      };
    }).filter((section) => section.entries.length > 0); 
  }, [kanbanWorkflows, workflows, hasPermission, hasNavAccess]);

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: 0.5, position: 'relative', zIndex: 1 }}>
      
      {/* TABLEAU DE BORD (Pour Admin / Manager) */}
      {canViewDashboard && (
        <List disablePadding sx={{ mb: 0.5 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => onNavigate('/')}
              sx={{
                minHeight: 48,
                pl: 3,
                pr: 2,
                py: 1,
                borderLeft: '3px solid transparent',
                transition: 'all 0.15s ease',
                ...(isDashboardActive && {
                  bgcolor: activeBgColor,
                  color: activeTextColor,
                  borderLeftColor: activeTextColor,
                  backgroundImage: activeGradient,
                }),
                ...(!isDashboardActive && {
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                    color: theme.palette.text.primary,
                    borderLeftColor: theme.palette.divider,
                  },
                }),
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 38, justifyContent: 'center' }}>
                <Dashboard sx={{ fontSize: '1.3rem' }} />
              </ListItemIcon>
              <ListItemText
                primary="Tableau de bord"
                primaryTypographyProps={{
                  component: 'div',
                  fontWeight: isDashboardActive ? 600 : 500,
                  fontSize: '0.95rem',
                  color: 'inherit',
                }}
              />
              {isDashboardActive && (
                <KeyboardArrowRight sx={{ fontSize: '1.1rem', opacity: 0.5, ml: 1 }} />
              )}
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {/* ACCUEIL EMPLOYÉ */}
      {!canViewDashboard && (
        <List disablePadding sx={{ mb: 0.5 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => onNavigate('/accueil')}
              sx={{
                minHeight: 48,
                pl: 3,
                pr: 2,
                py: 1,
                borderLeft: '3px solid transparent',
                transition: 'all 0.15s ease',
                ...(isAccueilActive && {
                  bgcolor: activeBgColor,
                  color: activeTextColor,
                  borderLeftColor: activeTextColor,
                  backgroundImage: activeGradient,
                }),
                ...(!isAccueilActive && {
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    bgcolor: theme.palette.action.hover,
                    color: theme.palette.text.primary,
                    borderLeftColor: theme.palette.divider,
                  },
                }),
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 38, justifyContent: 'center' }}>
                <Home sx={{ fontSize: '1.3rem' }} />
              </ListItemIcon>
              <ListItemText
                primary="Accueil"
                primaryTypographyProps={{
                  component: 'div',
                  fontWeight: isAccueilActive ? 600 : 500,
                  fontSize: '0.95rem',
                  color: 'inherit',
                }}
              />
              {isAccueilActive && (
                <KeyboardArrowRight sx={{ fontSize: '1.1rem', opacity: 0.5, ml: 1 }} />
              )}
            </ListItemButton>
          </ListItem>
        </List>
      )}

      {dynamicSections.map((section, idx) => (
        <Section
          key={section.sectionLabel ?? `section-${idx}`}
          section={section}
          onNavigate={onNavigate}
          currentPath={location.pathname}
          pendingLeavesCount={pendingLeavesCount}
          timesheetsCount={timesheetsEnAttenteCount}
          canManageTimesheets={canManageTimesheets}
          rejectedLeavesCount={rejectedLeavesCount}
          rejectedTimesheetsCount={rejectedTimesheetsCount}
        />
      ))}
    </Box>
  );
};

export default SidebarNavigation;